import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { AppLayout } from '../components/Layout';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import {
  TrendingUp, AlertCircle, CheckCircle2, Zap, ArrowRight, RefreshCw,
  Shield, Circle, Target, GraduationCap,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BAND = {
  on_track: { label: 'On Track', accent: '#22d3ee', glow: 'rgba(34,211,238,0.35)', badge: 'bg-cyan-500/10 border-cyan-400/40 text-cyan-200', desc: 'Strong momentum toward graduation and readiness.' },
  watch:    { label: 'Watch',    accent: '#a78bfa', glow: 'rgba(167,139,250,0.35)', badge: 'bg-violet-500/10 border-violet-400/40 text-violet-200', desc: 'A few gaps need attention. The actions below move your score fastest.' },
  at_risk:  { label: 'At Risk',  accent: '#f472b6', glow: 'rgba(244,114,182,0.35)', badge: 'bg-pink-500/10 border-pink-400/40 text-pink-200', desc: 'Several requirements are incomplete. Your counselor should review these gaps.' },
  critical: { label: 'Critical', accent: '#fb7185', glow: 'rgba(251,113,133,0.35)', badge: 'bg-rose-500/10 border-rose-400/40 text-rose-200', desc: 'Immediate attention required. Schedule a counselor meeting this week.' },
};

const S_ICON  = { complete: CheckCircle2, in_progress: TrendingUp, missing: AlertCircle };
const S_COLOR = { complete: 'text-cyan-300', in_progress: 'text-violet-300', missing: 'text-pink-300' };
const S_BAR   = { complete: '#22d3ee', in_progress: '#a78bfa', missing: '#f472b6' };

// ── Big half-circle gauge ────────────────────────────────────────────────────
function Gauge({ score, band }) {
  const cfg = BAND[band?.id] || BAND.watch;
  const circ = Math.PI * 118;
  const filled = (score / 100) * circ;

  return (
    <div className="flex flex-col items-center select-none">
      <div className="relative" style={{ filter: `drop-shadow(0 0 40px ${cfg.glow})` }}>
        <svg width="290" height="172" viewBox="0 0 290 172" aria-hidden>
          <path d="M 27 145 A 118 118 0 0 1 263 145"
            fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="20" strokeLinecap="round" />
          <motion.path
            d="M 27 145 A 118 118 0 0 1 263 145"
            fill="none" stroke={cfg.accent} strokeWidth="20" strokeLinecap="round"
            strokeDasharray={`${circ}`}
            initial={{ strokeDashoffset: circ }}
            animate={{ strokeDashoffset: circ - filled }}
            transition={{ duration: 1.3, ease: [0.16, 1, 0.3, 1] }}
          />
        </svg>
        <div className="absolute inset-x-0 bottom-6 flex flex-col items-center">
          <span className="font-outfit text-6xl font-bold text-white tracking-tight leading-none tabular-nums"
            data-testid="readiness-score">{score}</span>
          <span className="mt-1.5 text-[11px] uppercase tracking-[0.18em] text-slate-300">out of 100</span>
        </div>
      </div>
      <span className={`-mt-1 inline-flex items-center gap-2 rounded-full border px-5 py-2 text-sm font-semibold ${cfg.badge}`}>
        {band?.label || cfg.label}
      </span>
      <p className="mt-4 text-center text-sm text-slate-300 max-w-sm leading-relaxed">{cfg.desc}</p>
    </div>
  );
}

const Tile = ({ label, value, hint, tone = 'text-white' }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-5">
    <p className="text-[11px] uppercase tracking-[0.16em] text-slate-300 font-semibold">{label}</p>
    <p className={`font-outfit text-3xl font-semibold mt-2 ${tone}`}>{value}</p>
    {hint && <p className="text-xs text-slate-300/80 mt-1.5">{hint}</p>}
  </div>
);

// ── Factor card ──────────────────────────────────────────────────────────────
function FactorCard({ factor, index }) {
  const Icon  = S_ICON[factor.status]  || Circle;
  const color = S_COLOR[factor.status] || 'text-slate-300';
  const ptPct = factor.max ? Math.round((factor.points / factor.max) * 100) : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.05 + index * 0.04 }}
      className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 hover:border-cyan-400/30 hover:bg-white/[0.05] transition-colors"
      data-testid={`readiness-factor-${factor.id}`}
    >
      <div className="flex items-start gap-3">
        <Icon className={`h-5 w-5 shrink-0 mt-0.5 ${color}`} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white">{factor.label}</p>
          {factor.detail && <p className="text-xs text-slate-300 mt-1">{factor.detail}</p>}
        </div>
        <span className="text-sm font-semibold text-slate-300 tabular-nums shrink-0">
          {factor.points}<span className="text-slate-300">/{factor.max}</span>
        </span>
      </div>
      <div className="mt-4 h-1.5 rounded-full bg-white/[0.07] overflow-hidden">
        <motion.div className="h-1.5 rounded-full"
          initial={{ width: 0 }} animate={{ width: `${ptPct}%` }}
          transition={{ duration: 0.9, ease: 'easeOut', delay: 0.1 + index * 0.04 }}
          style={{ background: S_BAR[factor.status] || '#C6D4E8' }} />
      </div>
      {factor.action && (
        <Link to={factor.path || '#'}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-cyan-400 hover:text-cyan-300 mt-3 transition-colors">
          {factor.action} <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      )}
    </motion.div>
  );
}

export default function GraduationPredictionPage() {
  const { user, getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    axios.get(`${API}/graduation/prediction`, getAuthHeader())
      .then(r => setData(r.data))
      .catch(() => toast.error('Could not load readiness data'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const band = data?.band;
  const cfg  = BAND[band?.id] || BAND.watch;
  const earned = (data?.factors || []).reduce((a, f) => a + (f.points || 0), 0);
  const possible = (data?.factors || []).reduce((a, f) => a + (f.max || 0), 0);
  const missing = (data?.factors || []).filter(f => f.status === 'missing').length;

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto space-y-6 pb-16" data-testid="graduation-prediction-page">

        {/* ── Hero ─────────────────────────────────────────────────────── */}
        <motion.section
          initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden rounded-2xl border border-cyan-500/25 bg-[#0a1729]/70 px-7 py-6 md:px-8 md:py-7"
          style={{ boxShadow: '0 40px 100px -60px rgba(34,211,238,0.6)' }}>
          <div aria-hidden className="pointer-events-none absolute -top-24 -right-16 h-64 w-64 rounded-full blur-3xl opacity-30"
            style={{ background: `radial-gradient(circle, ${cfg.accent}, transparent 68%)` }} />
          <div aria-hidden className="pointer-events-none absolute -bottom-24 -left-16 h-56 w-56 rounded-full blur-3xl opacity-20"
            style={{ background: 'radial-gradient(circle, rgba(167,139,250,0.35), transparent 65%)' }} />
          <div className="relative flex flex-wrap items-end justify-between gap-6">
            <div className="min-w-0">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-cyan-300/40 bg-cyan-400/10 px-2.5 py-0.5
                text-[10px] font-semibold uppercase tracking-[0.2em] text-cyan-200">
                <GraduationCap className="h-3 w-3" /> Readiness Intelligence
              </span>
              <h1 className="font-outfit mt-3 text-3xl sm:text-4xl font-semibold text-white tracking-tight">
                {user?.first_name ? `${user.first_name}, are you on track?` : 'Are you on track?'}
              </h1>
              <p className="mt-2 max-w-2xl text-sm text-slate-300/90 leading-relaxed">
                One live score across every graduation and readiness requirement, recalculated the moment
                you complete something. Nothing here is a guess: each factor shows exactly what it's worth.
              </p>
            </div>
            <button onClick={load} disabled={loading} data-testid="readiness-refresh-btn"
              className="flex items-center gap-2 rounded-full border border-cyan-300/30 bg-cyan-500/[0.06] px-4 py-2 text-sm text-cyan-100 hover:border-cyan-300/60 hover:bg-cyan-500/[0.12] transition-colors">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </motion.section>

        {loading ? (
          <div className="flex justify-center py-24">
            <div className="h-9 w-9 animate-spin rounded-full border-2 border-cyan-400 border-t-transparent" />
          </div>
        ) : (
          <>
            {/* ── Score + summary ─────────────────────────────────────── */}
            <div className="grid gap-6 lg:grid-cols-[420px_1fr]">
              <motion.div
                initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }}
                className="relative overflow-hidden rounded-2xl border border-cyan-500/25 bg-[#0a1729]/60 px-8 py-10 flex flex-col items-center justify-center"
                style={{ boxShadow: '0 30px 80px -60px rgba(34,211,238,0.55)' }}>
                <div aria-hidden className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <div className="h-72 w-72 rounded-full opacity-[0.10]"
                    style={{ background: `radial-gradient(circle, ${cfg.accent}, transparent 70%)` }} />
                </div>
                <div className="relative"><Gauge score={data?.score || 0} band={band} /></div>
                <div className="relative mt-6 flex items-center gap-2 text-sm text-slate-300">
                  <Shield className="h-4 w-4 text-slate-300" />
                  {data?.complete_count}/{data?.total_factors} factors complete
                </div>
              </motion.div>

              <div className="space-y-6">
                <div className="grid gap-4 sm:grid-cols-3">
                  <Tile label="Points earned" value={`${earned}/${possible}`} hint="Across all readiness factors" />
                  <Tile label="Factors complete" value={`${data?.complete_count || 0}`} hint={`of ${data?.total_factors || 0} tracked`} tone="text-cyan-300" />
                  <Tile label="Still missing" value={`${missing}`} hint={missing ? 'Start with the actions below' : 'Nothing outstanding'} tone={missing ? 'text-violet-300' : 'text-cyan-300'} />
                </div>

                {data?.priority_actions?.length > 0 && (
                  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}
                    className="rounded-3xl border border-cyan-400/20 bg-[#0b1b30] overflow-hidden">
                    <div className="flex items-center gap-2.5 px-6 py-4 border-b border-white/[0.06]">
                      <Zap className="h-4 w-4 text-cyan-300" />
                      <h2 className="font-outfit text-base font-semibold text-white">Do these next, biggest score movers</h2>
                    </div>
                    <div className="p-4 space-y-2.5">
                      {data.priority_actions.map((action, i) => (
                        <Link key={i} to={action.path || '#'}
                          data-testid={`readiness-action-${i}`}
                          className="flex items-center gap-4 rounded-2xl border border-white/[0.07] bg-white/[0.025] px-5 py-4 hover:border-cyan-400/40 hover:bg-white/[0.06] transition-colors group">
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-cyan-400/30 bg-cyan-500/10 text-sm font-bold text-cyan-300">
                            {i + 1}
                          </span>
                          <span className="flex-1 text-sm text-slate-200">{action.action}</span>
                          <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-cyan-300 group-hover:translate-x-0.5 transition-all" />
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                )}
              </div>
            </div>

            {/* ── All factors ─────────────────────────────────────────── */}
            <section>
              <div className="flex items-center gap-2.5 mb-5">
                <Target className="h-4 w-4 text-cyan-300" />
                <h2 className="font-outfit text-xl font-semibold text-white">Every factor, and what it is worth</h2>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                {data?.factors?.map((f, i) => <FactorCard key={f.id} factor={f} index={i} />)}
              </div>
            </section>
          </>
        )}
      </div>
    </AppLayout>
  );
}
